import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FolderI } from 'src/app/models/folder';
import { PROJECT_NAME_MAX_LENGTH, ProjectI } from 'src/app/models/project';
import { AllPublicationFolderE } from 'src/app/models/publication';
import { ProjectService } from 'src/app/online/projects/service/project.service';
import { PublicationPayloadService } from 'src/app/online/publication/service/publicationPayload.service';
import {
    FolderSortFieldE,
    PublicationSaveTypeE,
    SortDirectionT
} from '../../publication-save-in-folder-dialog/publication-save-in-folder-dialog.component';

export interface ProjectSelectorSelectionI {
    projectId?: number;
    folderId?: number;
}

const PROJECT_FILE_PUBLICATION_TYPE_BY_SAVE_TYPE: Record<PublicationSaveTypeE, AllPublicationFolderE> = {
    [PublicationSaveTypeE.DOCUMENT]: AllPublicationFolderE.DOCUMENT,
    [PublicationSaveTypeE.BOOK]: AllPublicationFolderE.BOOKPART,
    [PublicationSaveTypeE.WHOLE_BOOK]: AllPublicationFolderE.BOOK
};

@Component({
    selector: 'app-project-selector',
    templateUrl: './project-selector.component.html',
    styleUrls: ['./project-selector.component.scss']
})
export class ProjectSelectorComponent implements OnInit {
    readonly FolderSortFieldE = FolderSortFieldE;
    readonly projectNameMaxLength = PROJECT_NAME_MAX_LENGTH;

    @Input() publicationId?: number;
    @Input() publicationType: PublicationSaveTypeE = PublicationSaveTypeE.DOCUMENT;
    // The parent knows the up-to-date state of the publication (eg. a book's current bookParts,
    // or a document's current version) and is the only one able to tell whether a folder that
    // already contains the publication is holding a stale copy of it.
    @Input() isFolderOutdated?: (folder: FolderI) => boolean;
    @Input() updateOutdatedFolder?: (folder: FolderI) => Promise<void>;

    @Output() selectionChange = new EventEmitter<ProjectSelectorSelectionI>();

    @ViewChild('newItemInput') private newItemInput: ElementRef<HTMLInputElement>;

    projects: ProjectI[] = [];
    filteredProjects: ProjectI[] = [];
    projectsLoading = false;

    openedProject?: ProjectI;
    folders: FolderI[] = [];
    foldersLoading = false;

    searchText = '';
    sortField: FolderSortFieldE = FolderSortFieldE.NAME;
    sortDirection: SortDirectionT = 'asc';

    selectedProjectId?: number;
    selectedFolderId?: number;

    creatingItem = false;
    newItemName = '';
    creatingItemLoading = false;

    constructor(private projectService: ProjectService, private publicationPayloadService: PublicationPayloadService) {}

    ngOnInit(): void {
        this.fetchProjects();
    }

    fetchProjects(): void {
        this.projectsLoading = true;
        this.projectService.getProjects().subscribe({
            next: projects => {
                this.projects = projects;
                this.projectsLoading = false;
                this.updateFilteredProjects();
            },
            error: () => (this.projectsLoading = false)
        });
    }

    openProject(project: ProjectI): void {
        this.openedProject = project;
        this.fetchFolders(project);
    }

    backToProjects(): void {
        this.openedProject = undefined;
        this.folders = [];
    }

    onSearchTextChange(searchText: string): void {
        this.searchText = searchText;
        this.updateFilteredProjects();
    }

    onSortFieldChange(sortField: FolderSortFieldE): void {
        this.sortField = sortField;
        this.updateFilteredProjects();
    }

    toggleSortDirection(): void {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        this.updateFilteredProjects();
    }

    selectProject(project: ProjectI): void {
        if (this.isProjectAlreadyContainingPublication(project)) {
            return;
        }
        this.selectedProjectId = project.id;
        this.selectedFolderId = undefined;
        this.selectionChange.emit({ projectId: project.id });
    }

    onFolderSelect(folder: FolderI): void {
        this.selectedFolderId = folder.id;
        this.selectedProjectId = this.openedProject?.id;
        this.selectionChange.emit({ projectId: this.selectedProjectId, folderId: folder.id });
    }

    onFolderCreated(folder: FolderI): void {
        if (this.openedProject) {
            this.openedProject.folders = [folder, ...this.openedProject.folders];
        }
        this.folders = [folder, ...this.folders];
    }

    isProjectAlreadyContainingPublication(project: ProjectI): boolean {
        if (!this.publicationId) {
            return false;
        }
        const projectFilePublicationType = PROJECT_FILE_PUBLICATION_TYPE_BY_SAVE_TYPE[this.publicationType];
        return !!project.fileReferences?.some(
            reference =>
                reference.publicationId === this.publicationId &&
                reference.publicationType === projectFilePublicationType
        );
    }

    startCreatingItem(): void {
        this.creatingItem = true;
        this.newItemName = '';
        setTimeout(() => this.newItemInput?.nativeElement.focus());
    }

    cancelCreatingItem(): void {
        this.creatingItem = false;
        this.newItemName = '';
    }

    confirmCreateItem(): void {
        const name = this.newItemName?.trim();
        if (!name || this.creatingItemLoading) {
            return;
        }
        this.creatingItemLoading = true;
        this.createProject(name);
    }

    private fetchFolders(project: ProjectI): void {
        this.foldersLoading = true;
        this.publicationPayloadService.prepareStore(project.id).subscribe({
            next: result => {
                this.folders = result.folderList || [];
                this.foldersLoading = false;
            },
            error: () => (this.foldersLoading = false)
        });
    }

    private createProject(name: string): void {
        this.projectService.createProject(name).subscribe({
            next: project => {
                this.projects = [project, ...this.projects];
                this.creatingItemLoading = false;
                this.creatingItem = false;
                this.updateFilteredProjects();
                this.selectProject(project);
            },
            error: () => (this.creatingItemLoading = false)
        });
    }

    private updateFilteredProjects(): void {
        const searchText = this.searchText?.trim().toLowerCase() || '';
        const projects = (this.projects || []).filter(
            project => !searchText || (project.name || '').toLowerCase().includes(searchText)
        );
        this.filteredProjects = projects.sort((a, b) => this.compareItems(a.name, a.createdAt, b.name, b.createdAt));
    }

    private compareItems(
        nameA: string,
        createdAtA: string | undefined,
        nameB: string,
        createdAtB: string | undefined
    ): number {
        let comparison: number;
        if (this.sortField === FolderSortFieldE.NAME) {
            comparison = (nameA || '').localeCompare(nameB || '');
        } else {
            comparison = this.timestamp(createdAtA) - this.timestamp(createdAtB);
        }
        return this.sortDirection === 'asc' ? comparison : -comparison;
    }

    private timestamp(date?: string): number {
        return date ? new Date(date).getTime() : 0;
    }
}
